import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { generateSecurePassword, validatePassword } from '../../../utils/password.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import { canManageClinic, HUB_USER_MANAGE_DENIED_MESSAGE } from '../../../utils/hubPermissions.js';

/**
 * ユーザー管理ページ（クリニック内）
 */
const UsersManagement = () => {
  const { user, clinic } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const canManage = canManageClinic(user);
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [inviteData, setInviteData] = useState({
    userId: '',
    displayName: '',
    password: '',
    role: 'doctor',
    isActive: true
  });

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [roleList, setRoleList] = useState([]); // 選択可能ロール（API取得）
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [resultIsClosing, setResultIsClosing] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(buildApiUrl('/api/roles'), { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && Array.isArray(data.roles)) setRoleList(data.roles);
      })
      .catch(() => { if (!cancelled) setRoleList([]); });
    return () => { cancelled = true; };
  }, []);

  const getRoleLabel = (role) => {
    if (role === 'admin') return '管理者';
    const found = roleList.find((r) => r.value === role);
    return found ? found.label : (role || '');
  };

  const getRoleColor = (role) => {
    if (role === 'admin') return 'var(--hub-warning)';
    return 'var(--hub-accent-primary)';
  };

  const isDuplicateUserId = (userId, currentUid) => {
    const normalized = (userId || '').trim().toLowerCase();
    if (!normalized) return false;
    return users.some((u) => {
      if (currentUid && u.uid === currentUid) return false;
      return (u.userId || '').trim().toLowerCase() === normalized;
    });
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await fetch(buildApiUrl('/api/clinic/users'), {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'ユーザー一覧の取得に失敗しました');
      }
      const normalizedUsers = (data.users || []).map((u) => ({
        ...u,
        isCurrentUser: u.uid === user?.uid
      }));
      setUsers(normalizedUsers);
    } catch (err) {
      setUsersError(err.message || 'ユーザー一覧の取得に失敗しました');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!canManage) {
      showToast('user.error', { message: HUB_USER_MANAGE_DENIED_MESSAGE });
      return;
    }
    const trimmedUserId = inviteData.userId.trim();
    if (!trimmedUserId) {
      showToast('user.error', { message: 'ユーザーIDは必須です。' });
      return;
    }
    if (!clinic?.id) {
      showToast('user.error', { message: 'クリニック情報が取得できません。' });
      return;
    }
    if (!editingUser) {
      const pwdCheck = validatePassword(inviteData.password);
      if (!pwdCheck.valid) {
        showToast('user.error', {
          message: pwdCheck.message || 'パスワードは8文字以上・英数字記号で入力してください。'
        });
        return;
      }
    }

    if (isDuplicateUserId(trimmedUserId, editingUser?.uid)) {
      showToast('user.error', { message: '同じクリニック内で同じユーザーIDが既に使われています。' });
      return;
    }
    setLoading(true);
    try {
      if (editingUser) {
        const response = await fetch(buildApiUrl('/api/clinic/update-user'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            uid: editingUser.uid,
            displayName: inviteData.displayName,
            role: inviteData.role,
            isActive: inviteData.isActive
          })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'ユーザー更新に失敗しました');
        }
        showToast('user.update.success');
      } else {
        const response = await fetch(buildApiUrl('/api/clinic/create-user'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: '',
            password: inviteData.password,
            userId: trimmedUserId,
            displayName: inviteData.displayName,
            role: inviteData.role
          })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'ユーザー作成に失敗しました');
        }
        setResultData({
          clinicName: clinic?.name || '',
          clinicCode: clinic?.code || '',
          userId: trimmedUserId,
          password: inviteData.password,
          displayName: inviteData.displayName,
          role: inviteData.role
        });
        setShowResultModal(true);
        showToast('user.create.success');
      }

      setShowInviteModal(false);
      setEditingUser(null);
      setInviteData({ userId: '', displayName: '', password: '', role: roleList[0]?.value || 'doctor', isActive: true });
      loadUsers();
    } catch (err) {
      showToast('user.error', { message: err.message || '操作に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInvite = () => {
    if (!canManage) return;
    setEditingUser(null);
    const defaultRole = roleList[0]?.value || 'doctor';
    setInviteData({
      userId: '',
      displayName: '',
      password: generateSecurePassword(12, { includeSpecial: true }),
      role: defaultRole,
      isActive: true
    });
    setShowInviteModal(true);
  };

  const handleOpenEdit = (targetUser) => {
    if (!canManage) return;
    setEditingUser(targetUser);
    setInviteData({
      userId: targetUser.userId || '',
      displayName: targetUser.displayName || '',
      password: '',
      role: targetUser.role || 'doctor',
      isActive: targetUser.isActive !== false
    });
    setShowInviteModal(true);
  };

  const handleCloseModal = () => {
    setShowInviteModal(false);
    setEditingUser(null);
  };

  useEffect(() => {
    if (searchParams.get('open') !== 'invite') return;
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
    if (!canManage) {
      showToast('user.error', { message: HUB_USER_MANAGE_DENIED_MESSAGE });
      return;
    }
    handleOpenInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canManage]);

  useEffect(() => {
    if (canManage) return;
    setShowInviteModal(false);
    setEditingUser(null);
    setShowDeleteModal(false);
    setDeleteTargetUser(null);
  }, [canManage]);

  const handleOpenDelete = (u) => {
    if (!canManage) return;
    setDeleteTargetUser(u);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTargetUser(null);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetUser || !canManage) return;
    setLoading(true);
    setDeleteError('');
    try {
      const response = await fetch(buildApiUrl('/api/clinic/delete-user'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ uid: deleteTargetUser.uid })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'ユーザー削除に失敗しました');
      }
      handleCloseDeleteModal();
      loadUsers();
      showToast('user.delete.success');
    } catch (err) {
      setDeleteError(err.message || '削除に失敗しました');
      showToast('user.error', { message: err.message || '削除に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseResultModal = () => {
    setResultIsClosing(true);
    setTimeout(() => {
      setShowResultModal(false);
      setResultIsClosing(false);
      setResultData(null);
      setCopySuccess(false);
    }, 200);
  };

  const handleCopyLoginInfo = async () => {
    if (!resultData) return;
    const textToCopy =
      `クリニック: ${resultData.clinicName}\n` +
      (resultData.clinicCode ? `クリニックコード: ${resultData.clinicCode}\n` : '') +
      `ユーザーID: ${resultData.userId}\n` +
      `パスワード: ${resultData.password}\n` +
      `表示名: ${resultData.displayName || '未設定'}\n` +
      `ロール: ${getRoleLabel(resultData.role)}`;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
      showToast('clipboard.copy.success');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      showToast('clipboard.copy.success');
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleToggleActive = async (targetUser) => {
    if (!canManage) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/clinic/update-user'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          uid: targetUser.uid,
          isActive: !targetUser.isActive
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'ユーザー更新に失敗しました');
      }
      loadUsers();
      showToast('user.toggle.success', {
        message: targetUser.isActive ? 'ユーザーを無効化しました' : 'ユーザーを有効化しました'
      });
    } catch (err) {
      showToast('user.error', { message: err.message || 'ユーザー更新に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    loadUsers();
  }, [user?.uid]);

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">ユーザー管理</h1>
        <button
          onClick={handleOpenInvite}
          className="hub-btn hub-btn-primary"
          disabled={!canManage}
          title={!canManage ? HUB_USER_MANAGE_DENIED_MESSAGE : undefined}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <line x1="20" y1="8" x2="20" y2="14"/>
            <line x1="23" y1="11" x2="17" y2="11"/>
          </svg>
          ユーザーを追加
        </button>
      </div>

      <div className="hub-main-content">
        {!canManage && (
          <div style={{
            background: 'var(--hub-warning-light)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hub-space-sm)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20, color: 'var(--hub-warning)' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span style={{ color: 'var(--hub-text-primary)' }}>
              {HUB_USER_MANAGE_DENIED_MESSAGE}
            </span>
          </div>
        )}

        {/* ユーザー数 */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-1" data-panel-index="0" style={{ marginBottom: 'var(--hub-space-xl)' }}>
          {usersLoading ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="hub-shimmer" style={{ width: '100px', height: '0.8125rem', marginBottom: '8px' }} />
                <div className="hub-shimmer" style={{ width: '80px', height: '2rem' }} />
              </div>
              <div className="hub-shimmer" style={{ width: '120px', height: '8px', borderRadius: '4px' }} />
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                  登録ユーザー数
                </div>
                <div style={{
                  fontFamily: 'var(--hub-font-display)',
                  fontSize: '2rem',
                  fontWeight: 800
                }}>
                  {users.length}
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--hub-text-muted)' }}>
                    {clinic?.userLimit ? ` / ${clinic.userLimit}` : ''}
                  </span>
                </div>
              </div>
              {clinic?.userLimit && (
                <div style={{
                  width: '120px',
                  height: '8px',
                  background: 'var(--hub-border)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min(100, (users.length / clinic.userLimit) * 100)}%`,
                    height: '100%',
                    background: users.length >= clinic.userLimit ? 'var(--hub-danger)' : 'var(--hub-accent-primary)',
                    borderRadius: '4px'
                  }} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* ユーザー一覧 */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-2" data-panel-index="1">
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.125rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-lg)'
          }}>
            メンバー
          </h2>

          {usersLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-sm)' }}>
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className="hub-shimmer"
                  style={{ height: '72px', borderRadius: 'var(--hub-radius-md)' }}
                />
              ))}
            </div>
          ) : usersError ? (
            <div style={{ color: 'var(--hub-text-secondary)' }}>
              {usersError}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-sm)' }}>
              {users.length === 0 ? (
                <div style={{ color: 'var(--hub-text-secondary)' }}>
                  ユーザーが登録されていません。
                </div>
              ) : users.map((u) => (
                <div 
                  key={u.uid}
                  className="hub-user-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--hub-space-md)',
                    background: u.isCurrentUser ? 'var(--hub-accent-primary-light)' : 'var(--hub-bg-secondary)',
                    borderRadius: 'var(--hub-radius-md)',
                    border: u.isCurrentUser ? '1px solid var(--hub-accent-primary)' : '1px solid transparent'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hub-space-md)' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: u.isActive ? 'var(--hub-gradient-primary)' : 'var(--hub-border)',
                      borderRadius: 'var(--hub-radius-full)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--hub-text-inverse)',
                      fontWeight: 600,
                      flexShrink: 0
                    }}>
                      {u.displayName?.charAt(0) || u.userId?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--hub-space-sm)'
                      }}>
                        <span style={{ fontWeight: 600 }}>{u.displayName || u.userId}</span>
                        {u.isCurrentUser && (
                          <span style={{
                            background: 'var(--hub-accent-primary)',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: 'var(--hub-radius-full)',
                            fontSize: '0.6875rem',
                            fontWeight: 600
                          }}>
                            あなた
                          </span>
                        )}
                        {!u.isActive && (
                          <span style={{
                            background: 'var(--hub-danger-light)',
                            color: 'var(--hub-danger)',
                            padding: '2px 8px',
                            borderRadius: 'var(--hub-radius-full)',
                            fontSize: '0.6875rem',
                            fontWeight: 600
                          }}>
                            無効
                          </span>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '0.8125rem',
                        color: 'var(--hub-text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--hub-space-sm)'
                      }}>
                        <span>@{u.userId}</span>
                        <span>•</span>
                        <span style={{ color: getRoleColor(u.role) }}>{getRoleLabel(u.role)}</span>
                      </div>
                    </div>
                  </div>

                  {!u.isCurrentUser && (
                    <div className="hub-user-row-actions" style={{ display: 'flex', gap: 'var(--hub-space-sm)' }}>
                      <button
                        className="hub-btn hub-btn-ghost hub-btn-sm"
                        onClick={() => handleOpenEdit(u)}
                        disabled={!canManage || loading}
                        title={!canManage ? HUB_USER_MANAGE_DENIED_MESSAGE : undefined}
                      >
                        編集
                      </button>
                      <button
                        className="hub-btn hub-btn-ghost hub-btn-sm"
                        style={{ color: u.isActive ? 'var(--hub-warning)' : 'var(--hub-success)' }}
                        onClick={() => handleToggleActive(u)}
                        disabled={!canManage || loading}
                        title={!canManage ? HUB_USER_MANAGE_DENIED_MESSAGE : undefined}
                      >
                        {u.isActive ? '無効化' : '有効化'}
                      </button>
                      <button
                        className="hub-btn hub-btn-ghost hub-btn-sm"
                        style={{ color: 'var(--hub-danger)' }}
                        onClick={() => handleOpenDelete(u)}
                        disabled={!canManage || loading}
                        title={!canManage ? HUB_USER_MANAGE_DENIED_MESSAGE : undefined}
                      >
                        削除
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 追加モーダル */}
        {showInviteModal && canManage && (
          <div 
            className="hub-modal-overlay"
          >
            <div 
              className="hub-card"
              style={{ 
                width: '100%',
                maxWidth: '480px',
                maxHeight: '90vh',
                overflow: 'auto',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.8) 100%)'
              }}
            >
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--hub-space-xl)'
              }}>
                <h2 style={{ 
                  fontFamily: 'var(--hub-font-display)',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  margin: 0
                }}>
                  {editingUser ? 'ユーザーを編集' : 'ユーザーを追加'}
                </h2>
                <button 
                  onClick={handleCloseModal}
                  className="hub-btn hub-btn-ghost"
                  style={{ padding: '8px' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleInvite}>
                <div className="hub-form-group">
                  <label className="hub-form-label hub-form-required">ユーザーID</label>
                  <input
                    type="text"
                    className="hub-form-input"
                    value={inviteData.userId}
                    readOnly={!!editingUser}
                    disabled={!!editingUser}
                    onChange={editingUser ? undefined : (e) => setInviteData({ ...inviteData, userId: e.target.value })}
                    placeholder="例: yamada"
                    required
                    style={editingUser ? { backgroundColor: 'var(--hub-bg-secondary, #f0f0f0)', cursor: 'not-allowed' } : undefined}
                  />
                  {!editingUser && (
                    <span className="hub-form-hint">ログイン時に使用します</span>
                  )}
                </div>

                <div className="hub-form-group">
                  <label className="hub-form-label">表示名</label>
                  <input
                    type="text"
                    className="hub-form-input"
                    value={inviteData.displayName}
                    onChange={(e) => setInviteData({ ...inviteData, displayName: e.target.value })}
                    placeholder="例: 山田 太郎"
                  />
                </div>

                {!editingUser && (
                  <div className="hub-form-group">
                    <label className="hub-form-label hub-form-required">パスワード</label>
                    <input
                      type="text"
                      className="hub-form-input"
                      value={inviteData.password}
                      onChange={(e) => setInviteData({ ...inviteData, password: e.target.value })}
                      placeholder="8文字以上・英数字記号"
                      required
                      minLength={8}
                    />
                    <span className="hub-form-hint">8文字以上・英数字記号で設定してください</span>
                  </div>
                )}

                <div className="hub-form-group">
                  <label className="hub-form-label">ロール</label>
                  <select
                    className="hub-form-select"
                    value={inviteData.role}
                    onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                    disabled={roleList.length === 0}
                  >
                    {roleList.length === 0 ? (
                      <option value={inviteData.role}>読み込み中...</option>
                    ) : (
                      roleList.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))
                    )}
                  </select>
                </div>

                {editingUser && (
                  <div className="hub-form-group">
                    <label className="hub-form-label">ステータス</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--hub-space-sm)' }}>
                      <input
                        type="checkbox"
                        checked={inviteData.isActive}
                        onChange={(e) => setInviteData({ ...inviteData, isActive: e.target.checked })}
                      />
                      <span>ユーザーを有効にする</span>
                    </label>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 'var(--hub-space-md)', marginTop: 'var(--hub-space-xl)' }}>
                  <button 
                    type="button"
                    onClick={handleCloseModal}
                    className="hub-btn hub-btn-secondary"
                    style={{ flex: 1 }}
                  >
                    キャンセル
                  </button>
                  <button 
                    type="submit"
                    className="hub-btn hub-btn-primary"
                    style={{ flex: 1 }}
                    disabled={loading || roleList.length === 0}
                  >
                    {loading ? <LoadingSpinner size="small" /> : (editingUser ? '更新する' : '追加する')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ユーザー追加完了モーダル */}
        {showResultModal && resultData && (
          <div
            className="hub-modal-overlay hub-result-modal-overlay"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
            onClick={handleCloseResultModal}
          >
            <div
              className="hub-result-modal"
              style={{
                backgroundColor: 'var(--hub-surface, #fff)',
                color: 'var(--hub-text, #1a1a1a)',
                borderRadius: 12,
                padding: 24,
                maxWidth: 420,
                width: '90%',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                border: '1px solid var(--hub-border, #e0e0e0)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>ユーザー追加完了</h2>
                <button type="button" onClick={handleCloseResultModal} className="hub-btn hub-btn-ghost" style={{ padding: 8 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: 'var(--hub-text-muted, #666)' }}>クリニック</span>
                  <span>{resultData.clinicName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: 'var(--hub-text-muted, #666)' }}>ユーザーID</span>
                  <span style={{ fontFamily: 'monospace' }}>{resultData.userId}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: 'var(--hub-text-muted, #666)' }}>パスワード</span>
                  <span style={{ fontFamily: 'monospace' }}>{resultData.password}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: 'var(--hub-text-muted, #666)' }}>表示名</span>
                  <span>{resultData.displayName || '未設定'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: 'var(--hub-text-muted, #666)' }}>ロール</span>
                  <span>{getRoleLabel(resultData.role)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={handleCopyLoginInfo}
                  className={copySuccess ? 'hub-btn hub-btn-primary' : 'hub-btn hub-btn-secondary'}
                  style={{ flex: 1 }}
                >
                  {copySuccess ? 'コピーしました' : 'ログイン情報をコピー'}
                </button>
                <button type="button" onClick={handleCloseResultModal} className="hub-btn hub-btn-primary" style={{ flex: 1 }}>
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 削除確認モーダル */}
        {showDeleteModal && deleteTargetUser && canManage && (
          <div
            className="hub-modal-overlay"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
            onClick={handleCloseDeleteModal}
          >
            <div
              style={{
                backgroundColor: 'var(--hub-surface, #fff)',
                color: 'var(--hub-text, #1a1a1a)',
                borderRadius: 12,
                padding: 24,
                maxWidth: 400,
                width: '90%',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                border: '1px solid var(--hub-border, #e0e0e0)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 12px', fontSize: '1.125rem' }}>ユーザーを削除しますか？</h3>
              <p style={{ margin: '0 0 20px', color: 'var(--hub-text-muted, #666)', fontSize: '0.9375rem' }}>
                {deleteTargetUser.displayName || deleteTargetUser.userId} を削除すると、ログインできなくなります。この操作は取り消せません。
              </p>
              {deleteError && (
                <p style={{ margin: '0 0 12px', color: 'var(--hub-danger)', fontSize: '0.875rem' }}>{deleteError}</p>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={handleCloseDeleteModal} className="hub-btn hub-btn-secondary">
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="hub-btn"
                  style={{ background: 'var(--hub-danger)', color: '#fff' }}
                  disabled={loading}
                >
                  {loading ? <LoadingSpinner size="small" /> : '削除する'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UsersManagement;

